import * as API from '@/API';
import { type FilingCabinetContextData } from '@/Contexts/Authenticated/FilingCabinetContext';
import { EntityLoading, EntityNotLoaded, createLoadedEntity, isEntityLoaded, isEntityNotLoaded, type Entity, type EntityLoaded } from '@/Models/Entity';
import { type FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import { Label } from '@/Models/GenericWorklist/Label';
import {
    WorklistColumnViewParam,
    WorklistFilterViewParam,
    type Worklist,
    type WorklistFilterParams,
    type WorklistPaginationParams,
    type WorklistSortingParam,
} from '@/Models/GenericWorklist/WorklistParams';
import type { OrderForWorklist, WorklistResult, BulkReportingAction } from '@/Models/GenericWorklist/WorklistResult';
import type { WorklistInitialData } from '@/Models/GenericWorklist/WorklistSideBarData';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import { t } from 'i18next';
import { action, makeAutoObservable, runInAction } from 'mobx';
import { cloneDeep } from 'lodash';
import { PopupProcedureReportInformation, ProcedureReportInformation } from '@/Models/PopupProcedureReportInformation';
import { createDefaultWorklist, WORKLIST_SETUP_DIALOG_FILTER_TAB } from '@/Pages/GenericWorklist/Stores/WorklistStoreDefaults';
import { CustomWorklist, normalizeWorklistDates } from '@/Models/GenericWorklist/CustomWorklist';
import { worklistHasMinimumCriteria } from '@/Pages/GenericWorklist/Logic/WorklistFilterCriteriaLogic';
import { clearHiddenFilterFields } from '@/Pages/GenericWorklist/Logic/WorklistFilterPropertyMapping';

interface StateData {
    worklistLoaded: boolean;
    initialPageData: Entity<WorklistInitialData>;
    worklist: Worklist;
    results: Entity<WorklistResult>;
    selectedIds: number[];
    selectedCustomWorklistID: number | null;
}

// Controls how a results fetch affects the current row selection.
export interface FetchResultsOptions {
    // When true, the current selection is kept instead of being cleared, and afterwards
    // reconciled to drop any ids that are no longer part of the refreshed results. Use
    // this for "refresh" style fetches (returning to the worklist, manual reload) where
    // the result set's meaning hasn't changed. Leave false (default) for fetches where
    // the meaning of the result set changes (filter/paging/sorting), which should reset
    // the selection.
    preserveSelection?: boolean;
}

const createDefaultState = (): StateData => {
    const defaultWorklist = createDefaultWorklist();

    return {
        worklistLoaded: false,
        worklist: defaultWorklist,
        initialPageData: EntityNotLoaded.instance,
        results: EntityNotLoaded.instance,
        selectedCustomWorklistID: null,
        selectedIds: [],
    };
};

export interface IWorklistStore {
    readonly filterTooBroad: boolean;
    readonly fullCount: number | null;
    readonly fullCountOverflow: boolean;
    readonly initialPageData: Entity<WorklistInitialData>;
    readonly isWorklistLoaded: boolean;
    readonly isReturningWorklist: boolean;
    readonly orders: OrderForWorklist[];
    readonly results: Entity<WorklistResult>;
    readonly worklist: Worklist;
    readonly availableLabels: Label[];
    readonly selectedIds: number[];
    readonly selectedOrders: OrderForWorklist[];
    applyFilter: (filter: WorklistFilterParams | undefined, getResults?: boolean) => void;
    applyPaging: (pagingParams: WorklistPaginationParams) => void;
    applySorting: (worklistSorting: WorklistSortingParam[] | undefined) => void;
    fetchResultForCurrentParams: (options?: FetchResultsOptions) => Promise<void>;
    openColumnsSetupDialog: (tab: string) => void;
    setReportDialogOpen: (open: boolean) => void;
    refetchResults: (options?: FetchResultsOptions) => Promise<void>;
    resetWorklistToDefault: () => void;
    setColumnsSetupDialogTab: (idx: string) => void;
    setColumnViewSettings: (data: WorklistColumnViewParam[]) => void;
    setFilterViewSettings: (data: WorklistFilterViewParam[]) => void;
    setInitialPageData: (pageData: Entity<WorklistInitialData>) => void;
    setTitle: (title: string) => void;
    setPath: (path: string) => void;
    setWorklist: (worklist: Worklist) => void;
    selectedCustomWorklistID: number | null;
    setSelectedCustomWorklistID: (id: number | null) => void;
    setSelection: (selectedIds: number[]) => void;
    clearSelection: () => void;
    disposeStore: () => void;
    openReportDialog: (orderId: number) => Promise<PopupProcedureReportInformation | null>;
    getOrderById: (orderId: number) => OrderForWorklist | undefined;
    setWorklistLoaded: (loaded: boolean) => void;
    setSidebarCollapsed: (worklist: CustomWorklist, value: boolean) => void;
    classifySelectedOrders: () => BulkReportingAction[];
}

export default class WorklistStore implements IWorklistStore {
    columnsSetupDialogTab = WORKLIST_SETUP_DIALOG_FILTER_TAB;
    columnsSetupModalOpen = false;
    reportDialogOpen = false;
    reportUpgradeProcedureDialogOpen = false;
    reportUpgradeProcedure: ProcedureReportInformation | null = null;

    // Set once at construction time (see initOld/initNew) and never mutated afterwards,
    // so it reliably reflects whether this store instance was restored from a
    // previously-used worklist (as opposed to a brand new one) - regardless of what
    // `results` transitions to afterwards (e.g. due to an aborted in-flight fetch).
    // Used to decide whether to refresh on mount when returning to the worklist.
    isReturningWorklist = false;

    private state!: StateData;
    private reportAbortController: AbortController | null = null;
    private initialDataAbortController: AbortController | null = null;
    private _filterTooBroad = false;

    private _reportDialogInfo: PopupProcedureReportInformation | null = null;
    private _reportDialogProcedures: ProcedureReportInformation[] | null = null;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(initialState: FilingCabinetItem, fcContext: FilingCabinetContextData) {
        makeAutoObservable(this);

        this.fcStore = new FilingCabinetStore<StateData>(initialState, fcContext);

        this.fcStore.initializeState(this.initOld, this.initNew);
    }

    private readonly initOld = (state: StateData): void => {
        this.isReturningWorklist = !isEntityNotLoaded(state.results);
        this.setState({
            ...state,
            worklist: normalizeWorklistDates(state.worklist),
        });
        this.fcStore.updateTitle(`${t('Worklist')}`);
    };

    private readonly initNew = (): void => {
        const state = createDefaultState();
        this.setState(state);
        this.fcStore.updateTitle(`${t('Worklist')}`);
    };

    get results(): Entity<WorklistResult> {
        return this.state.results;
    }

    get filterTooBroad(): boolean {
        return this._filterTooBroad;
    }

    get worklist(): Worklist {
        return this.state.worklist;
    }

    get initialPageData(): Entity<WorklistInitialData> {
        return this.state.initialPageData;
    }

    get isWorklistLoaded(): boolean {
        return this.state.worklistLoaded;
    }

    get fullCount(): number | null {
        if (isEntityLoaded(this.results)) {
            return this.results.Count;
        }
        return null;
    }

    get fullCountOverflow(): boolean {
        if (isEntityLoaded(this.results)) {
            return this.results.CountOverflow;
        }
        return false;
    }

    get orders(): OrderForWorklist[] {
        if (isEntityLoaded(this.results)) {
            return this.results.Orders;
        }
        return [];
    }

    setTitle = (title: string): void => {
        this.fcStore.updateTitle(title);
    };

    setPath = (path: string): void => {
        this.fcStore.updatePath(path);
    };

    get selectedCustomWorklistID(): number | null {
        return this.state.selectedCustomWorklistID;
    }

    setSelectedCustomWorklistID = (id: number | null): void => {
        this.setState({ ...this.state, worklistLoaded: false, selectedCustomWorklistID: id });
    };

    // -----------------------------------
    // Worklist Filter Params
    // -----------------------------------
    applyFilter = (filter: WorklistFilterParams | undefined, getResults = true): void => {
        this.state.worklist.Params.Filter = filter;
        this.resetPaging();
        this.fcStore.updateState(this.state);

        if (!getResults) {
            return;
        }
        void this.fetchResultForCurrentParams();
    };

    applyPaging = (pagingParams: WorklistPaginationParams): void => {
        this.state.worklist.Params.Pagination = pagingParams;

        this.fcStore.updateState(this.state);
        void this.fetchResultForCurrentParams();
    };

    resetPaging = (): void => {
        this.state.worklist.Params.Pagination = {
            ...this.state.worklist.Params.Pagination,
            Offset: 0,
        };
        this.fcStore.updateState(this.state);
    };

    applySorting = (worklistSorting: WorklistSortingParam[] | undefined): void => {
        this.setWorklist({
            ...this.worklist,
            Params: {
                ...this.worklist.Params,
                Sorting: worklistSorting,
            },
        });
        void this.fetchResultForCurrentParams();
    };

    refetchResults = async (options?: FetchResultsOptions): Promise<void> => {
        await this.fetchResultForCurrentParams(options);
    };

    // -----------------------------------
    // Worklist View Params
    // -----------------------------------
    setColumnViewSettings = (data: WorklistColumnViewParam[]): void => {
        this.state.worklist.View.Columns = data;

        this.fcStore.updateState(this.state);
    };

    setFilterViewSettings = (data: WorklistFilterViewParam[]): void => {
        const currentFilters = this.state.worklist.View.Filters;
        const newlyHiddenProperties = data
            .filter((p) => !p.Visible && currentFilters.find((f) => f.ID === p.ID)?.Visible !== false)
            .map((p) => p.ID);

        if (newlyHiddenProperties.length > 0) {
            this.state.worklist.Params.Filter = clearHiddenFilterFields(
                this.state.worklist.Params.Filter,
                newlyHiddenProperties,
            );
        }

        this.state.worklist.View.Filters = data;
        this.fcStore.updateState(this.state);
    };

    // -----------------------------------
    // UI state
    // -----------------------------------
    openColumnsSetupDialog = (tab: string): void => {
        this.columnsSetupDialogTab = tab;
        this.columnsSetupModalOpen = true;
    };

    setColumnsSetupDialogTab = (idx: string): void => {
        this.columnsSetupDialogTab = idx;
    };

    setColumnsSetupModalOpen = (open: boolean): void => {
        this.columnsSetupModalOpen = open;
    };

    setReportDialogOpen = (open: boolean): void => {
        this.reportDialogOpen = open;
    };

    setReportProcedureUpgradeDialogOpen = (open: boolean): void => {
        this.reportUpgradeProcedureDialogOpen = open;
    };

    setReportProcedureToBeUpgraded = (procedure: ProcedureReportInformation): void => {
        this.reportUpgradeProcedure = procedure;
    };

    setWorklist = (worklist: Worklist): void => {
        this.applyWorklist(worklist);
    };

    // Applies the worklist state and persists it without triggering a fetch.
    // Use this when the caller will handle fetching separately.
    applyWorklist = (worklist: Worklist): void => {
        this.setState({
            ...this.state,
            worklistLoaded: true,
            worklist: cloneDeep(worklist),
        });
        this.fcStore.updateState(this.state);
    };

    resetWorklistToDefault = (): void => {
        this.applyWorklist(createDefaultWorklist());
    };

    setInitialPageData = (pageData: Entity<WorklistInitialData>): void => {
        this.setState({ ...this.state, worklistLoaded: false, initialPageData: pageData });
    };

    initializeStore = async (): Promise<void> => {
        if (!isEntityLoaded(this.initialPageData)) {
            await this.loadInitialData();
        }
    };

    disposeStore = (): void => {
        this.reportAbortController?.abort();
        this.initialDataAbortController?.abort();
    };

    private readonly loadInitialData = async (): Promise<void> => {
        this.initialDataAbortController?.abort();

        this.initialDataAbortController = new AbortController();
        const signal = this.initialDataAbortController.signal;

        this.setInitialPageData(EntityLoading.instance);
        try {
            const initialPageData = await API.worklist.getInitialPageData(signal);
            this.setInitialPageData(createLoadedEntity(initialPageData));
        }
        catch (error: unknown) {
            if (error instanceof Error && error.name !== 'AbortError' && !signal.aborted) {
                this.setInitialPageData(EntityNotLoaded.instance);
            }
            throw error;
        }
    };

    private readonly setResults = action((entity: EntityLoading | EntityNotLoaded | EntityLoaded<WorklistResult>, options?: FetchResultsOptions): void => {
        this.state.results = entity;

        if (!options?.preserveSelection) {
            this.clearSelection(); // Filter/paging/sorting changes the meaning of the result set, so reset selection
        }
        else if (isEntityLoaded(entity)) {
            // Refreshing (e.g. returning to the worklist, or a manual reload): keep the
            // selection, but drop any ids that are no longer part of the refreshed results
            const presentIds = new Set(entity.Orders.map((order) => order.OrderInfo.OrderID));
            this.state.selectedIds = this.state.selectedIds.filter((id) => presentIds.has(id));
        }

        this.fcStore.updateState(this.state);
    });

    fetchResultForCurrentParams = async (options?: FetchResultsOptions): Promise<void> => {
        this.reportAbortController?.abort();

        if (!worklistHasMinimumCriteria(this.state.worklist.Params.Filter)) {
            this.reportAbortController = null;
            this._filterTooBroad = true;
            this.setResults(createLoadedEntity({ Orders: [], Count: 0, CountOverflow: false }));
            return;
        }

        this._filterTooBroad = false;
        this.reportAbortController = new AbortController();
        const signal = this.reportAbortController.signal;

        this.setResults(EntityLoading.instance, options);

        try {
            const result = await API.worklist.get(this.state.worklist.Params, signal);
            this.setResults(createLoadedEntity(result), options);
        }
        catch (error: unknown) {
            if (error instanceof Error && error.name !== 'AbortError' && !signal.aborted) {
                this.setResults(EntityNotLoaded.instance, options);
            }
            throw error;
        }
    };

    private readonly setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(newState);
    };

    setSelection(selectedIds: number[]): void {
        this.setState({ ...this.state, selectedIds });
    }

    clearSelection(): void {
        this.setState({
            ...this.state,
            selectedIds: [],
        });
    }

    get selectedIds(): number[] {
        return this.state.selectedIds;
    }

    get selectedOrders(): OrderForWorklist[] {
        if (!this.state.selectedIds || this.state.selectedIds.length === 0) {
            return [];
        }
        return this.orders.filter((x) => this.state.selectedIds.includes(x.OrderInfo.OrderID));
    }

    get availableLabels(): Label[] {
        if (isEntityLoaded(this.initialPageData)) {
            return this.initialPageData.LabelsToInclude;
        }
        return [];
    }

    getOrderById = (orderId: number): OrderForWorklist | undefined => {
        return this.orders.find((order) => order.OrderInfo.OrderID === orderId);
    };

    upgradeProcedureStatus = (orderProcedureId: number | undefined): void => {
        if (orderProcedureId == undefined) {
            console.error('Cannot upgrade order procedure with unknown Id');
            return;
        }
        void API.reports.upgradeProcedure(orderProcedureId)
            .then((res) => {
                runInAction(() => {
                    if (!res.Done) {
                        console.error(`Something went wrong while upgrading order procedure with id ${orderProcedureId}`);
                        return;
                    }
                    this.setReportProcedureUpgradeDialogOpen(false);

                    const data = this.procedureReportData?.find((x) => x.OrderHasProcedure_UID === orderProcedureId);
                    if (!data) {
                        return;
                    }
                    data.Status_Text = 'Completed';
                    data.UpgradeAvailable = false;
                    data.AvailableForReport = true;
                });
            });
    };

    get procedureReportData(): ProcedureReportInformation[] | null {
        return this._reportDialogProcedures;
    }

    get reportDialogInfo(): PopupProcedureReportInformation | null {
        return this._reportDialogInfo;
    }

    get reportDialogProcedures(): ProcedureReportInformation[] | null {
        if (this._reportDialogProcedures === null) {
            return null;
        }

        if (this.selectedIds.length <= 1) { // In case we just want to go to a report for a single order, consider all choices
            return this._reportDialogProcedures;
        }

        // In case of a bulk situation we want to hide the "to validate" reports because they don't make sense for the possible dictation selection
        // In ReportDialog.tsx we will then show the to dictate procedures if there's a necessary choice to be made
        const toDictate = this._reportDialogProcedures.filter((x) => x.Report_UID === 0);
        if (toDictate.length > 0) {
            return toDictate;
        }

        return null;
    }

    openReportDialog = async (orderId: number): Promise<PopupProcedureReportInformation | null> => {
        const res = await API.reports.getProceduresForOrder(orderId);
        runInAction(() => {
            if (!res.ShowPopup) {
                return;
            }

            this.setReportDialogOpen(true);
            this._reportDialogInfo = { ...res, OrderId: orderId }; // Adding orderId to the data here, because otherwise we have no access to it (we lose this "connection" because the contextmenu closes and so resets the store's selectionIds which we were using)
            this._reportDialogProcedures = res.Items;
            this._reportDialogProcedures.forEach((x) => x.Checked = true);
        });

        if (res.ShowPopup) {
            return null;
        }

        return res;
    };

    toggleReportProcedureChecked = (procedure: ProcedureReportInformation, checked: boolean): void => {
        const data = this._reportDialogProcedures?.find((x) => x.OrderHasProcedure_UID === procedure.OrderHasProcedure_UID);
        if (!data) {
            console.warn('Could not find procedure to toggle report checkbox', {
                orderHasProcedureId: procedure.OrderHasProcedure_UID,
            });
            return;
        }
        data.Checked = checked;
    };

    setWorklistLoaded = (loaded: boolean): void => {
        this.setState({ ...this.state, worklistLoaded: loaded });
    };

    setSidebarCollapsed = (worklist: CustomWorklist, value: boolean): void => {
        worklist.UserConfig = { ...worklist.UserConfig, IsSidebarCollapsed: value };
        this.fcStore.updateState(this.state);
    };

    classifySelectedOrders = (): BulkReportingAction[] => {
        const list: BulkReportingAction[] = [];
        this.selectedIds.forEach((id) => {
            const order = this.getOrderById(id);
            if (order === undefined) {
                return;
            }

            const reportIds = this.getReportIdsForOrder(order);

            if (reportIds.length >= 1 && order.OrderProceduresInfos.length === reportIds.length) { // Multiple reports for multiple procedures, but all procedures dictated
                reportIds.forEach((reportId) => {
                    list.push({ type: 'validate', orderId: id, reportId });
                });
            }
            else if (reportIds.length >= 1 && order.OrderProceduresInfos.length != reportIds.length) { // Multiple reports where one (or more) procedures still need dictating
                list.push({ type: 'dictate', orderId: id });
                reportIds.forEach((reportId) => {
                    list.push({ type: 'validate', orderId: id, reportId });
                });
            }
            else if (reportIds.length === 0) { // No reports ready yet for this order
                list.push({ type: 'dictate', orderId: id });
            }
        });
        return list;
    };

    private readonly getReportIdsForOrder = (order: OrderForWorklist | undefined): number[] => {
        if (order === undefined) {
            return [];
        }
        return order.OrderReportsInfos.map((x) => x.ReportID);
    };
}
